import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Pill, Package, ShoppingCart, TrendingUp,
  Users, Truck, Heart, UserCheck, FileText, Building2,
  UserCog, Shield, Calculator, Receipt, BarChart3, ClipboardList,
  Bell, ChevronLeft, ChevronRight, LogOut, Menu, User,
  Database, Settings, Landmark, ClipboardCheck, BookOpen, PackageSearch,
  RotateCcw,
} from 'lucide-react';
import useAuthStore from '../store/authStore';
import { settingsService } from '../api/services';

const navGroups = [
  { 
    items: [{ to:'/', icon:LayoutDashboard, label:'Dashboard' }]
  },
  { 
    items: [
      { to:'/medicines',         icon:Pill,           label:'Medicines' },
      { to:'/inventory',         icon:Package,         label:'Inventory' },
    ]
  },
  { 
    items: [
      { to:'/purchase-requests', icon:PackageSearch,   label:'Purchase Requests' },
      { to:'/requisitions',      icon:ClipboardCheck,  label:'Requisitions' },
      { to:'/purchases',         icon:ShoppingCart,    label:'Purchases' },
    ]
  },
  { 
    items: [
      { to:'/sales',             icon:TrendingUp,      label:'Sales / POS' },
      { to:'/sale-returns',      icon:RotateCcw,       label:'Sale Returns' },
    { to:'/purchase-returns',  icon:RotateCcw,       label:'Purchase Returns' },
    ]
  },
  { 
    items: [
      { to:'/suppliers',         icon:Truck,           label:'Suppliers' },
      { to:'/customers',         icon:Users,           label:'Customers' },
      { to:'/patients',          icon:Heart,           label:'Patients' },
      { to:'/employees',         icon:UserCheck,       label:'Employees' },
    ]
  },
  { 
    items: [
      { to:'/prescriptions',     icon:FileText,        label:'Prescriptions' },
    ]
  },
  { 
    items: [
      { to:'/accounting',        icon:Calculator,      label:'Accounting' },
      { to:'/partners',          icon:Landmark,        label:'Partners' },
      { to:'/bank-accounts',     icon:Landmark,        label:'Bank Accounts' },
      { to:'/expenses',          icon:Receipt,         label:'Expenses' },
    ]
  },
  { 
    items: [
      { to:'/branches',          icon:Building2,       label:'Branches' },
      { to:'/users',             icon:UserCog,         label:'Users' },
      { to:'/roles',             icon:Shield,          label:'Roles' },
    ]
  },
  { 
    items: [
      { to:'/reports',           icon:BarChart3,       label:'Reports' },
      { to:'/audit',             icon:ClipboardList,   label:'Audit Logs' },
    ]
  },
  { 
    items: [
      { to:'/master-data',       icon:Database,        label:'Master Data' },
      { to:'/settings',          icon:Settings,        label:'Settings' },
    ]
  },
];

export default function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState({});

  React.useEffect(() => {
    settingsService.getAll()
      .then(r => setSiteSettings(r.data.settings || {}))
      .catch(() => {});
  }, []);

  const siteName  = siteSettings.company_name || 'PharmaCare';

  React.useEffect(() => {
    if (siteName) document.title = siteName;
  }, [siteName]);
  const siteLogo  = siteSettings.company_logo || null;
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-white border-r border-gray-200">
      <div className={`flex items-center h-16 px-4 border-b border-gray-200 ${collapsed?'justify-center':'justify-between'}`}>
        {!collapsed && (
          <div className="flex items-center gap-2 min-w-0">
            {siteLogo
              ? <img src={siteLogo} alt="logo" className="w-8 h-8 rounded-lg object-contain flex-shrink-0 bg-white border border-gray-100"/>
              : <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0"><Pill size={18} className="text-white"/></div>
            }
            <div className="min-w-0">
              <p className="text-sm font-bold text-gray-900 leading-tight truncate max-w-[130px]">{siteName}</p>
              <p className="text-xs text-gray-500">Enterprise</p>
            </div>
          </div>
        )}
        {collapsed && (
          siteLogo
            ? <img src={siteLogo} alt="logo" className="w-8 h-8 rounded-lg object-contain bg-white border border-gray-100"/>
            : <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center"><Pill size={18} className="text-white"/></div>
        )}
        <button onClick={()=>setCollapsed(!collapsed)} className="p-1 rounded hover:bg-gray-100 hidden lg:block">
          {collapsed?<ChevronRight size={16} className="text-gray-500"/>:<ChevronLeft size={16} className="text-gray-500"/>}
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto py-4 px-2">
        {navGroups.map((group,gi)=>(
          <div key={gi} className="mb-3">
            {!collapsed && <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-2 mb-1">{group.label}</p>}
            {group.items.map(({to,icon:Icon,label})=>(
              <NavLink key={to} to={to} end={to==='/'} onClick={()=>setMobileOpen(false)}
                className={({isActive})=>`flex items-center gap-3 px-3 py-2 rounded-lg mb-0.5 text-sm font-medium transition-colors ${isActive?'bg-blue-50 text-blue-700':'text-gray-600 hover:bg-gray-100 hover:text-gray-900'} ${collapsed?'justify-center':''}`}
                title={collapsed?label:''}>
                <Icon size={18} className="flex-shrink-0"/>
                {!collapsed && <span>{label}</span>}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div className={`p-3 border-t border-gray-200 ${collapsed?'flex justify-center':''}`}>
        {!collapsed ? (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0"><User size={16} className="text-blue-700"/></div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-900 truncate">{user?.name||'User'}</p>
              <p className="text-xs text-gray-500 truncate">{user?.role?.name||'Role'}</p>
            </div>
            <button onClick={handleLogout} className="p-1 rounded hover:bg-gray-100" title="Logout"><LogOut size={16} className="text-gray-500"/></button>
          </div>
        ) : (
          <button onClick={handleLogout} className="p-2 rounded hover:bg-gray-100" title="Logout"><LogOut size={16} className="text-gray-500"/></button>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <div className={`hidden lg:block flex-shrink-0 transition-all duration-300 ${collapsed?'w-16':'w-60'}`}><SidebarContent/></div>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="fixed inset-0 bg-black/50" onClick={()=>setMobileOpen(false)}/>
          <div className="relative w-64 z-50"><SidebarContent/></div>
        </div>
      )}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-6 flex-shrink-0">
          <button onClick={()=>setMobileOpen(true)} className="lg:hidden p-2 rounded hover:bg-gray-100"><Menu size={20} className="text-gray-600"/></button>
          <div className="flex-1">
            {/* Page title from pharmacy name */}
          </div>
          <div className="flex items-center gap-3">
            <NavLink to="/audit" className="relative p-2 rounded-lg hover:bg-gray-100"><Bell size={20} className="text-gray-600"/></NavLink>
            <NavLink to="/settings" className="p-2 rounded-lg hover:bg-gray-100"><Settings size={20} className="text-gray-600"/></NavLink>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-100 cursor-pointer">
              <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center"><User size={14} className="text-blue-700"/></div>
              <span className="text-sm font-medium text-gray-700 hidden sm:block">{user?.name}</span>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 lg:p-6"><Outlet/></main>
      </div>
    </div>
  );
}
